<!-- components/dialogs/DuckDBQueryDialog.vue -->
<template>
  <v-dialog v-model="dialog" max-width="1200px" scrollable>
    <v-card>
      <v-card-title class="text-h5 d-flex align-center">
        <v-icon class="me-2">mdi-database</v-icon>
        DuckDB Query Tool
        <v-spacer></v-spacer>
        <v-btn 
          color="secondary" 
          variant="outlined" 
          size="small" 
          class="me-2" 
          @click="exportDatabase"
          :loading="isExporting"
        >
          <v-icon class="me-1">mdi-database-export</v-icon>
          Export to Zip
        </v-btn>
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text>
        <v-textarea v-model="query"
                    label="SQL"
                    rows="4"
                    auto-grow
                    hide-details
                    variant="outlined"
                    density="compact"
                    spellcheck="false"
                    class="query-input mb-2"
                    @keydown.ctrl.enter="runQuery"
                    @keydown.meta.enter="runQuery" />

        <div class="d-flex align-center ga-2 mb-3">
          <v-btn color="primary" size="small" :loading="isRunning" @click="runQuery">
            <v-icon class="me-1">mdi-play</v-icon>
            Run
          </v-btn>
          <span class="text-caption text-medium-emphasis">Ctrl/Cmd + Enter</span>
          <v-spacer></v-spacer>
          <v-btn v-for="shortcut in shortcuts"
                 :key="shortcut.label"
                 size="x-small"
                 variant="tonal"
                 class="text-none"
                 @click="query = shortcut.sql">
            {{ shortcut.label }}
          </v-btn>
        </div>

        <v-alert v-if="errorMessage" type="error" density="compact" variant="tonal" class="mb-3">
          {{ errorMessage }}
        </v-alert>

        <div v-if="resultColumns.length > 0">
          <div class="text-caption text-medium-emphasis mb-1">
            {{ resultRows.length }} row(s){{ truncated ? ' (first 200 shown)' : '' }}
          </div>
          <div class="result-wrapper">
            <v-table density="compact" fixed-header height="360">
              <thead>
                <tr>
                  <th v-for="column in resultColumns" :key="column">{{ column }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in resultRows" :key="index">
                  <td v-for="column in resultColumns" :key="column">{{ formatCell(row[column]) }}</td>
                </tr>
              </tbody>
            </v-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { exportPath } from '/config';

// Props and emits
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

// Setup local state
const store = useStore();
const dialog = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const isExporting = ref(false);
const errorMessage = ref('');

// Ad-hoc SQL runner - the fastest way to sanity check the asset model against
// real data (unique assets vs items, due buckets, column names).
const query = ref('SELECT * FROM unique_assets LIMIT 20');
const isRunning = ref(false);
const resultColumns = ref([]);
const resultRows = ref([]);
const truncated = ref(false);

const MAX_ROWS = 200;

const shortcuts = [
  { label: 'DESCRIBE assets', sql: 'DESCRIBE assets' },
  { label: 'Model counts', sql: `SELECT
  (SELECT COUNT(*) FROM assets) AS raw_items,
  (SELECT COUNT(*) FROM asset_items) AS model_items,
  (SELECT COUNT(*) FROM unique_assets) AS unique_assets,
  (SELECT COUNT(*) FROM unique_assets WHERE NOT hasAssetId) AS orphans,
  (SELECT SUM(itemCount) FROM unique_assets) AS sum_items` },
  { label: 'Due buckets', sql: 'SELECT dueBucket, COUNT(*) AS assets FROM filteredAssets GROUP BY 1 ORDER BY 1' },
  { label: 'Version', sql: 'SELECT version()' },
];

// Close the dialog
const close = () => {
  dialog.value = false;
};

// Get DuckDB connection from store
const getDBConnection = computed(() => store.getters.getDBConnection);

// Arrow hands back BigInt, Date and typed-array values - render them readably.
const formatCell = (value) => {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Date) return value.toISOString().slice(0, 19).replace('T', ' ');
  if (typeof value === 'object') return JSON.stringify(value);
  const text = String(value);
  return text.length > 120 ? `${text.slice(0, 117)}…` : text;
};

const runQuery = async () => {
  const conn = getDBConnection.value;
  if (!conn || !query.value.trim()) return;

  isRunning.value = true;
  errorMessage.value = '';
  try {
    // queryRaw bypasses the isDataLoaded gate and surfaces errors instead of
    // returning an empty result.
    const result = await conn.queryRaw(query.value);
    const rows = result.toArray().map((row) => row.toJSON());
    truncated.value = rows.length > MAX_ROWS;
    resultRows.value = rows.slice(0, MAX_ROWS);
    resultColumns.value = rows.length > 0 ? Object.keys(rows[0]) : [];
    if (rows.length === 0) {
      resultColumns.value = [];
      errorMessage.value = 'Query returned no rows.';
    }
  } catch (error) {
    console.error('DuckDB query error:', error);
    errorMessage.value = error.message || String(error);
    resultColumns.value = [];
    resultRows.value = [];
  } finally {
    isRunning.value = false;
  }
};

// Export entire DuckDB database as a .db file
const exportDatabase = async () => {
  try {
    isExporting.value = true;
    errorMessage.value = '';
    
    const conn = getDBConnection.value;
    if (!conn) {
      throw new Error('DuckDB connection not initialized');
    }
    
    // Create a temporary export directory
    const exportDir = `/tmp/export_${Date.now()}`;
    
    // Export the database to the temporary directory
    await conn.query(`EXPORT DATABASE '${exportDir}' (FORMAT PARQUET)`);
    
    // Get the list of exported files
    const db = store.getters.getDuckDBService.db; // Access the DuckDB instance from your store
    if (!db) {
      throw new Error('DuckDB instance not available');
    }
    
    // List the exported files
    const exportedFiles = await db.globFiles(`${exportDir}/*`);
    if (!exportedFiles || exportedFiles.length === 0) {
      throw new Error('No files were exported');
    }
    
    // Create a zip file containing all the exported files
    const JSZip = await import('jszip').then(m => m.default);
    const zip = new JSZip();
    
    // Generate zip filename and extract folder name (without .zip extension)
    const zipFilename = `duckdb_export_${new Date().toISOString().slice(0,19).replace(/:/g,'-')}.zip`;
    const extractedFolderName = zipFilename.replace('.zip', '');
    const fullExportPath = `${exportPath}${extractedFolderName}/`;
    
    // Add each file to the zip
    for (const file of exportedFiles) {
      try {
        // Use the copyFileToBuffer method mentioned in the GitHub issue
        const buffer = await db.copyFileToBuffer(file.fileName);
        if (buffer) {
          const filename = file.fileName.split('/').pop();
          
          // If this is the load.sql file, modify its contents to use the full export path
          if (filename === 'load.sql') {
            // Convert buffer to text, modify paths, then convert back
            const decoder = new TextDecoder();
            const loadSqlContent = decoder.decode(buffer);
            
            // Replace absolute export paths with the full download path
            // This regex finds paths like '/tmp/export_123456/' and replaces with the full export path
            const modifiedContent = loadSqlContent.replace(
              new RegExp(`'${exportDir.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/([^']+)'`, 'g'),
              `'${fullExportPath}$1'`
            );
            
            // Convert back to buffer and add to zip
            const encoder = new TextEncoder();
            const modifiedBuffer = encoder.encode(modifiedContent);
            zip.file(filename, modifiedBuffer);
          } else {
            // Add other files normally
            zip.file(filename, buffer);
          }
        }
      } catch (err) {
        console.warn(`Could not add file ${file.fileName} to export: ${err.message}`);
      }
    }
    
    // Generate the zip file
    const zipBlob = await zip.generateAsync({type: 'blob'});
    
    // Download the zip file
    const link = document.createElement('a');
    const url = URL.createObjectURL(zipBlob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', zipFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error("Database export error:", error);
    errorMessage.value = `Database export error: ${error.message}`;
  } finally {
    isExporting.value = false;
  }
};
</script>

<style scoped>
.query-input :deep(textarea) {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 13px;
  line-height: 1.5;
}

.result-wrapper {
  border: 1px solid rgba(127, 127, 127, 0.25);
  border-radius: 4px;
  overflow: hidden;
}

.result-wrapper :deep(td) {
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  white-space: nowrap;
}
</style>